import React, { useState, useEffect, useRef, useCallback } from 'react';
import dayjs from 'dayjs';
import CalendarPicker from '../calendar-picker/calendar-picker.component';
import { Box, Typography, useTheme } from '@mui/material';

const DATE_FORMAT = 'DD/MM/YYYY';

const DateField = ({
  value,
  minDate,
  label,
  icon: IconComponent,
  variant,
  ...otherProps
}) => {
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isAndroid = /Android/i.test(navigator.userAgent);
  const isMobile = isIOS || isAndroid;

  const [selectedDate, setSelectedDate] = useState(
    value ? (dayjs.isDayjs(value) ? value : dayjs(value)) : null
  );
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const { name, onChange } = otherProps;
  const ref = useRef(null);
  const mobileDatePickerRef = useRef(null);

  const handleOutsideClick = useCallback((event) => {
    if (ref.current && !ref.current.contains(event.target)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [handleOutsideClick]);

  const handleDateClick = (day) => {
    setSelectedDate(day);
    setIsOpen(false);

    if (onChange) {
      onChange({ target: { value: day, name } });
    }
  };

  const handleInputClick = () => {
    if (!isIOS && isMobile) {
      // Directly trigger the click on the native date input
      mobileDatePickerRef.current?.click();
    } else {
      setIsOpen(!isOpen);
    }
  };

  const handleNativeDateChange = (event) => {
    const newDate = dayjs(event.target.value);
    setSelectedDate(newDate);

    if (onChange) {
      onChange({ target: { value: newDate, name } });
    }
  };

  const containerRef = useRef(null);
  // For iOS, we need to handle the date input differently
  useEffect(() => {
    if (isIOS && containerRef.current) {
      // Add a click event listener to the container
      const container = containerRef.current;

      const handleContainerClick = () => {
        if (mobileDatePickerRef.current) {
          mobileDatePickerRef.current.focus();
          mobileDatePickerRef.current.click();
        }
      };

      container.addEventListener('click', handleContainerClick);

      return () => {
        container.removeEventListener('click', handleContainerClick);
      };
    }
  }, [isIOS]);

  return (
    <Box
      ref={(el) => {
        ref.current = el;
        containerRef.current = el;
      }}
      display="flex"
      flexDirection="row"
      alignItems="center"
      backgroundColor={
        variant === 'light grey'
          ? theme.palette.grey[50]
          : theme.palette.background.default
      }
      sx={{
        position: 'relative',
        borderRadius: '8px',
        cursor: 'pointer',
        padding: '0 1rem',
        minHeight: '45px',
        flex: 1,
        overflow: isOpen ? 'visible' : 'hidden',
        WebkitTapHighlightColor: 'transparent',
      }}
      width="100%"
      onClick={!isIOS ? handleInputClick : undefined}
      aria-expanded={isOpen}
      aria-label={`Selecionar ${label}`}
    >
      {isMobile && (
        <input
          id="mobile-date-input"
          ref={mobileDatePickerRef}
          type="date"
          onChange={handleNativeDateChange}
          min={
            minDate
              ? dayjs(minDate).format('YYYY-MM-DD')
              : dayjs().add(1, 'day').format('YYYY-MM-DD')
          }
          value={selectedDate ? selectedDate.format('YYYY-MM-DD') : ''}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            opacity: 0, // Make it invisible again
            cursor: 'pointer',
            zIndex: isIOS ? -1 : 3, // Put it behind for iOS, on top for Android
          }}
        />
      )}

      <Typography
        sx={{
          fontSize: theme.typography.body1.fontSize,
          color: theme.palette.primary.main,
          flexGrow: 1,
          pointerEvents: 'none',
        }}
      >
        {selectedDate ? selectedDate.format(DATE_FORMAT) : label}
      </Typography>
      <IconComponent
        style={{
          marginLeft: '1rem',
          color: 'black',
          pointerEvents: 'none',
        }}
      />

      {!isMobile && isOpen && (
        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 10,
            marginTop: '8px',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <CalendarPicker
            handleDateClick={handleDateClick}
            selectedDate={selectedDate}
            minDate={minDate}
          />
        </Box>
      )}
    </Box>
  );
};

export default DateField;
